import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface AutocompleteOption {
  key: string | number;
  label: string;
  value: string | number;
}

interface CustomAutocompleteProps {
  label?: string;
  placeholder?: string;
  options: AutocompleteOption[];
  selectedKey?: string | number;
  onSelectionChange?: (key: string | number | null) => void;
  isDisabled?: boolean;
  closeOnSelect?: boolean;
  contentBeforeInput?: React.ReactNode;
  contentInsideInput?: React.ReactNode;
  className?: string;
  description?: string;
  portal?: boolean;
}

const CustomAutocomplete: React.FC<CustomAutocompleteProps> = ({
  label,
  placeholder = 'Buscar...',
  options,
  selectedKey,
  onSelectionChange,
  isDisabled = false,
  closeOnSelect = true,
  contentBeforeInput,
  contentInsideInput,
  className = '',
  description,
  portal = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [displayValue, setDisplayValue] = useState('');
  const [filteredOptions, setFilteredOptions] =
    useState<AutocompleteOption[]>(options);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isTyping, setIsTyping] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [portalPosition, setPortalPosition] = useState<{
    left: number;
    top?: number;
    bottom?: number;
    width: number;
    maxHeight: number;
  } | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Update display value when selectedKey changes
  useEffect(() => {
    if (isTyping) return;

    if (selectedKey) {
      const selectedOption = options.find(
        (option) => option.key === selectedKey
      );
      if (selectedOption) {
        setDisplayValue(selectedOption.label);
        setSearchTerm(selectedOption.label);
      }
    } else {
      setDisplayValue('');
      setSearchTerm('');
    }
  }, [selectedKey, options, isTyping]);

  // Filter options based on search term - only when typing
  useEffect(() => {
    if (!isTyping || !searchTerm.trim()) {
      setFilteredOptions(options);
    } else {
      const filtered = options.filter((option) =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredOptions(filtered);
    }
    setHighlightedIndex(-1);
  }, [searchTerm, options, isTyping]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        !dropdownRef.current?.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setIsTyping(false);
        setIsFocused(false);
        // Reset search term to selected value when clicking outside
        if (selectedKey) {
          const selectedOption = options.find(
            (option) => option.key === selectedKey
          );
          if (selectedOption) {
            setSearchTerm(selectedOption.label);
            setDisplayValue(selectedOption.label);
          }
        } else {
          setSearchTerm('');
          setDisplayValue('');
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [selectedKey, options]);

  useEffect(() => {
    if (!isOpen || !portal) {
      setPortalPosition(null);
      return;
    }

    const updatePortalPosition = () => {
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const viewportPadding = 8;
      const dropdownGap = 4;
      const spaceBelow =
        window.innerHeight - rect.bottom - viewportPadding - dropdownGap;
      const spaceAbove = rect.top - viewportPadding - dropdownGap;
      const openAbove = spaceBelow < 160 && spaceAbove > spaceBelow;
      const availableSpace = openAbove ? spaceAbove : spaceBelow;

      setPortalPosition({
        left: Math.max(
          viewportPadding,
          Math.min(rect.left, window.innerWidth - rect.width - viewportPadding)
        ),
        ...(openAbove
          ? {
              bottom: window.innerHeight - rect.top + dropdownGap,
            }
          : { top: rect.bottom + dropdownGap }),
        width: Math.min(rect.width, window.innerWidth - viewportPadding * 2),
        maxHeight: Math.max(96, Math.min(240, availableSpace)),
      });
    };

    updatePortalPosition();
    window.addEventListener('resize', updatePortalPosition);
    window.addEventListener('scroll', updatePortalPosition, true);
    return () => {
      window.removeEventListener('resize', updatePortalPosition);
      window.removeEventListener('scroll', updatePortalPosition, true);
    };
  }, [isOpen, options, portal]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setDisplayValue(value);
    setIsTyping(true);
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const handleOptionSelect = (option: AutocompleteOption) => {
    if (closeOnSelect) {
      setSearchTerm(option.label);
      setDisplayValue(option.label);
      setIsOpen(false);
      setIsTyping(false);
      setIsFocused(false);
    } else {
      setSearchTerm('');
      setDisplayValue('');
      setIsOpen(true);
      setIsTyping(false);
      setHighlightedIndex(-1);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
    onSelectionChange?.(option.key);
  };

  const handleInputFocus = () => {
    if (!isDisabled) {
      setIsFocused(true);
      setIsOpen(true);
      // Clear the input to show all options and allow typing
      setDisplayValue('');
      setSearchTerm('');
      setIsTyping(false);
    }
  };

  const handleInputBlur = () => {
    setIsFocused(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (isDisabled) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          handleOptionSelect(filteredOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setIsTyping(false);
        setIsFocused(false);
        if (selectedKey) {
          const selectedOption = options.find(
            (option) => option.key === selectedKey
          );
          if (selectedOption) {
            setSearchTerm(selectedOption.label);
            setDisplayValue(selectedOption.label);
          }
        } else {
          setSearchTerm('');
          setDisplayValue('');
        }
        break;
    }
  };

  // Scroll highlighted option into view
  useEffect(() => {
    if (highlightedIndex >= 0 && dropdownRef.current) {
      const highlightedElement = dropdownRef.current.children[
        highlightedIndex
      ] as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth',
        });
      }
    }
  }, [highlightedIndex]);

  const dropdown = isOpen && !isDisabled && (
    <div
      ref={dropdownRef}
      data-react-aria-top-layer={portal ? true : undefined}
      className={
        portal
          ? 'fixed z-[10050] overflow-auto rounded-lg border border-divider bg-background shadow-lg'
          : 'absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-divider bg-background shadow-lg'
      }
      style={
        portal && portalPosition
          ? {
              left: portalPosition.left,
              top: portalPosition.top,
              bottom: portalPosition.bottom,
              width: portalPosition.width,
              maxHeight: portalPosition.maxHeight,
            }
          : undefined
      }
    >
      {filteredOptions.length > 0 ? (
        filteredOptions.map((option, index) => (
          <div
            key={option.key}
            className={`px-3 py-2 cursor-pointer text-sm transition-colors ${
              highlightedIndex === index
                ? 'bg-blue-50 text-blue-600'
                : 'hover:bg-default-200'
            } ${
              selectedKey === option.key
                ? 'bg-blue-500 text-white hover:bg-blue-600'
                : ''
            }`}
            onPointerDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
              handleOptionSelect(option);
            }}
            onMouseEnter={() => setHighlightedIndex(index)}
          >
            {option.label}
          </div>
        ))
      ) : (
        <div className='px-3 py-2 text-sm text-default-400 italic'>
          No se encontraron resultados
        </div>
      )}
    </div>
  );

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Label */}
      {label && (
        <label className='block text-sm font-medium text-default-600 mb-1'>
          {label}
        </label>
      )}
      {contentBeforeInput}

      {/* Input Container */}
      <div
        className={`relative ${
          contentInsideInput
            ? `flex min-h-10 flex-wrap items-center gap-1 rounded-lg border border-divider bg-background px-2 py-1 pr-10 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500 ${
                isDisabled
                  ? 'cursor-not-allowed bg-default-100 text-default-400'
                  : 'hover:border-default-400'
              }`
            : ''
        }`}
      >
        {contentInsideInput}
        <input
          ref={inputRef}
          type='text'
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          disabled={isDisabled}
          placeholder={placeholder}
          className={
            contentInsideInput
              ? 'min-w-24 flex-1 border-0 bg-transparent py-1 text-sm outline-none placeholder:text-default-400 focus:outline-none'
              : `
                w-full px-3 py-2 pr-10
                border border-divider rounded-lg
                bg-background text-sm
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                disabled:bg-default-100 disabled:text-default-400 disabled:cursor-not-allowed
                ${isFocused ? 'ring-2 ring-blue-500 border-blue-500' : ''}
                ${isDisabled ? 'bg-default-100 text-default-400 cursor-not-allowed' : 'hover:border-default-400'}
              `
          }
        />

        {/* Dropdown Arrow */}
        <div className='absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none'>
          <svg
            className={`w-4 h-4 text-default-300 transition-transform duration-200 ${
              isOpen ? 'rotate-180' : ''
            }`}
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M19 9l-7 7-7-7'
            />
          </svg>
        </div>
      </div>

      {/* Description */}
      {description && (
        <p className='mt-1 text-xs text-default-400'>{description}</p>
      )}

      {portal && portalPosition
        ? createPortal(dropdown, document.body)
        : dropdown}
    </div>
  );
};

export default CustomAutocomplete;
